import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  WebPreEnrollConfig,
  ConfigApi,
  Configuration,
} from '@clear-denver/tpc-openapi-typescript/build/v1/lookup'

export const mockConfig = {
  locations: {
    displayConversionCouponInfo: true,
  },
  prePay: {
    enabled: true,
  },
  youngAdultDiscount: {
    isPromoActive: true,
    showBanner: true,
    showModule: true,
  },
}

type ConfigValue = string | number | boolean
type DynamicConfigEntry = { [configGroup: string]: ConfigValue }
type DynamicConfiguration = DynamicConfigEntry & WebPreEnrollConfig

const isConfig = (x: unknown): x is DynamicConfiguration => {
  return (
    typeof x === 'object' &&
    x !== null &&
    !Array.isArray(x) &&
    Object.keys(x).length > 0
  )
}

export type ConfigContextValues = {
  refetchConfig: () => void
  config: DynamicConfiguration
}

const initialState: ConfigContextValues = {
  refetchConfig: () => null,
  config: null,
}

export const ConfigContext = createContext<ConfigContextValues>(initialState)

export const ConfigProvider = ({
  children,
}: Readonly<{
  children: React.ReactNode
}>) => {
  const [config, setConfig] = useState<DynamicConfiguration>()

  const configClient = useMemo(
    () =>
      new ConfigApi(
        new Configuration({
          basePath: process.env.NEXT_PUBLIC_LOOKUP_API_ENDPOINT,
        })
      ),
    []
  )

  const fetchConfig = useCallback(async () => {
    const res = await configClient.getWebPreEnrollConfigRaw()
    await res.raw
      .json()
      .then((config: unknown) => {
        if (isConfig(config)) {
          setConfig(config)
        }
      })
      .catch(() => {
        /**
         * should always be valid JSON
         */
      })
  }, [configClient])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return (
    <ConfigContext.Provider value={{ config, refetchConfig: fetchConfig }}>
      {children}
    </ConfigContext.Provider>
  )
}

export const useConfigContext = () => useContext(ConfigContext)
